import React, { useState, useRef, useEffect } from 'react';
import Icon from '../Icon';
import Button from '../Button';
import dayjs from 'dayjs';

import i18n from '../../i18n';
import PropTypes from 'prop-types';
import styles from './ObjectEntry.module.scss';
import classNames from 'classnames';
import ShareModal from '../ShareModal';
import InfoBox from '../InfoBox';
import MassInfo from '../MassInfo';
import { trackEvent } from '../../utils/datalayer';
import { ReactComponent as CloseIcon } from '../Icon/icons/close.svg';
import { useTranslation } from 'next-i18next';
import ListOccurrences from '../ListOccurrences';
import EventIcon from '../EventIcon';
import LabeledIcon from '../LabeledIcon';

const ObjectEntry = React.forwardRef(
    (
        {
            id = null,
            title = null,
            slug = '',
            href = '/',
            image = null,
            text = null,
            label = null,
            removeItem = null,
            showRemoveButton = false,
            active = false,
            onClick = () => {},
            onMouseOver = () => {},
            addToMyList = () => {},
            isMyList = false,
            massInfo = [],
            date = '',
            isInMyList,
            infoBox,
            infoBoxText,
            closestStation,
            venueName,
            occurrences,
            category,
            isGrey,
            relatedObjects = {},
            isGuidePage = false,
            hideCursor = false,
        },
        ref
    ) => {
        const [readMore, setReadMore] = useState(false);
        const textElement = useRef(null);
        const [modalIsOpen, setModalOpen] = useState();
        const [removed, setRemoved] = useState(false);
        const { t } = useTranslation();

        const handleClose = (e) => {
            setModalOpen(false);
            e.stopPropagation();
        };

        const handleOpenModal = (e) => {
            setModalOpen(true);
            e.stopPropagation();
        };

        const handleRemoveItem = (id) => {
            setRemoved(true);
            removeItem(id);
        };

        const handleAddToMyList = (e) => {
            addToMyList(id);
            e.stopPropagation();
        };

        const handleReadMore = () => {
            setReadMore(!readMore);
            if (!readMore) {
                trackEvent(
                    'VirtualObjectPageView',
                    'Object',
                    'Read more',
                    slug
                );
            }
        };

        const dangerousText = { __html: text };

        const handleOnClick = (e) => {
            onClick(id);
            e.stopPropagation();
        };

        const hasImage = Boolean(image && image.renditions);
        if (removed) {
            return null;
        }

        const showMoreButton =
            (text && text.length > 30) || infoBox || infoBoxText;

        const lastOccurrence =
            occurrences && occurrences.length > 1
                ? occurrences[occurrences.length - 1]
                : null;

        const lastOccurrenceDate = lastOccurrence ? lastOccurrence.date : null;

        const isGreyInfoBox = isGrey ? 'Gray' : 'White';

        return (
            <div
                ref={ref}
                aria-expanded={readMore}
                className={classNames(styles['ObjectEntry'], {
                    [styles['ObjectEntry--Active']]: active,
                    [styles['ObjectEntry--Expanded']]: readMore,
                    [styles['ObjectEntry--HasStatusBox']]: !!venueName,
                    [styles['ObjectEntry--Grey']]: isGrey,
                    [styles['ObjectEntry--HideCursor']]: hideCursor,
                })}>
                <div
                    className={styles['ObjectEntry__Container']}
                    onClick={(e) => handleOnClick(e)}>
                    <div
                        className={styles['ObjectEntry__MobileClickHandler']}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleReadMore(e);
                        }}
                    />

                    {hasImage && (
                        <div className={styles['ObjectEntry__ImageContainer']}>
                            <div
                                className={
                                    styles['ObjectEntry__PictureCategory']
                                }>
                                {!!category?.title && (
                                    <div
                                        className={classNames(
                                            styles[
                                                'ObjectEntry__CategoryContainer'
                                            ]
                                        )}>
                                        <EventIcon icon={category.icon} />
                                        <span
                                            className={
                                                styles[
                                                    'ObjectEntry__CategoryText'
                                                ]
                                            }>
                                            {category?.title}
                                        </span>
                                    </div>
                                )}
                            </div>
                            <div
                                className={styles['ObjectEntry__Image']}
                                style={{
                                    backgroundImage: `url(${image.renditions.small.src})`,
                                    backgroundPosition: `${image.focal.x} ${image.focal.y}`,
                                }}></div>
                            {!!image.caption && (
                                <figcaption
                                    className={styles['ObjectEntry__Caption']}>
                                    {t('generic.photo')} {image.caption}
                                </figcaption>
                            )}
                        </div>
                    )}

                    <div
                        className={classNames(
                            styles['ObjectEntry__ContentContainer'],
                            {
                                [styles[
                                    'ObjectEntry__ContentContainer--NoImage'
                                ]]: !hasImage,
                            }
                        )}>
                        <div className={styles['ObjectEntry__Content']}>
                            {title && (
                                <h3 className={styles['ObjectEntry__Title']}>
                                    {title}
                                </h3>
                            )}
                            {isMyList && showRemoveButton && (
                                <div
                                    className={
                                        styles[
                                            'ObjectEntry__RemoveButtonDesktop'
                                        ]
                                    }>
                                    <button
                                        className={
                                            styles['ObjectEntry__RemoveButton']
                                        }
                                        aria-label="Remove"
                                        type="button"
                                        size="small"
                                        onClick={() => {
                                            handleRemoveItem(id);
                                        }}>
                                        <div
                                            className={
                                                styles[
                                                    'ObjectEntry__RemoveButtonIcon'
                                                ]
                                            }>
                                            <CloseIcon />
                                        </div>
                                        <div
                                            className={
                                                styles[
                                                    'ObjectEntry__ButtonLabel'
                                                ]
                                            }>
                                            {t('generic.remove')}
                                        </div>
                                    </button>
                                </div>
                            )}
                            {label && (
                                <p className={styles['ObjectEntry__Label']}>
                                    {label}
                                </p>
                            )}
                            {date && (
                                <div
                                    className={
                                        styles['ObjectEntry__MetaContainer']
                                    }>
                                    {date && (
                                        <div
                                            className={
                                                styles['ObjectEntry__DateRange']
                                            }>
                                            <Icon
                                                size="sm"
                                                type="calender"
                                                color="gray700"
                                            />
                                            <time
                                                dateTime={date}
                                                className={
                                                    styles[
                                                        'ObjectEntry__DateContainer'
                                                    ]
                                                }>
                                                <span
                                                    className={
                                                        styles[
                                                            'ObjectEntry__Date'
                                                        ]
                                                    }>
                                                    {dayjs(date).format(
                                                        'D MMM'
                                                    )}
                                                </span>
                                                {lastOccurrenceDate && (
                                                    <>
                                                        <span
                                                            className={
                                                                styles[
                                                                    'ObjectEntry__Date'
                                                                ]
                                                            }>
                                                            —
                                                        </span>
                                                        <span
                                                            className={
                                                                styles[
                                                                    'ObjectEntry__Date'
                                                                ]
                                                            }>
                                                            {dayjs(
                                                                lastOccurrenceDate
                                                            ).format('D MMM')}
                                                        </span>
                                                    </>
                                                )}
                                            </time>
                                        </div>
                                    )}
                                    {venueName && (
                                        <div
                                            className={
                                                styles['ObjectEntry__VenueName']
                                            }>
                                            <Icon
                                                size="sm"
                                                type="place"
                                                color="gray700"
                                            />
                                            <span>{venueName}</span>
                                        </div>
                                    )}
                                </div>
                            )}

                            {dangerousText && (
                                <React.Fragment>
                                    {showMoreButton && (
                                        <div
                                            className={
                                                styles[
                                                    'ObjectEntry__ReadMoreMobile'
                                                ]
                                            }>
                                            <Button
                                                type="secondaryPink"
                                                icon={
                                                    readMore ? 'minus' : 'plus'
                                                }
                                                iconColor="pink"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleReadMore(e);
                                                }}
                                                text={
                                                    readMore
                                                        ? t('generic.showLess')
                                                        : t('generic.showMore')
                                                }
                                            />{' '}
                                        </div>
                                    )}
                                    <div
                                        className={
                                            styles['ObjectEntry__TextDesktop']
                                        }>
                                        <p
                                            ref={textElement}
                                            className={classNames(
                                                styles['ObjectEntry__Text'],
                                                {
                                                    [styles[
                                                        'ObjectEntry__Text--Expanded'
                                                    ]]: readMore,
                                                    [styles[
                                                        'ObjectEntry__Text--Event'
                                                    ]]: date,
                                                }
                                            )}
                                            dangerouslySetInnerHTML={
                                                dangerousText
                                            }
                                        />
                                    </div>
                                    {!!relatedObjects.count &&
                                        isGuidePage &&
                                        readMore && (
                                            <div
                                                className={classNames(
                                                    styles[
                                                        'ObjectEntry__Banner'
                                                    ],
                                                    styles[
                                                        'ObjectEntry__Banner--Desktop'
                                                    ]
                                                )}>
                                                <a
                                                    href={relatedObjects.href}
                                                    className={
                                                        styles[
                                                            'ObjectEntry__BannerLink'
                                                        ]
                                                    }
                                                />
                                                <span
                                                    className={
                                                        styles[
                                                            'ObjectEntry__BannerText'
                                                        ]
                                                    }>
                                                    {t(
                                                        'Object.relatedObjects',
                                                        {
                                                            count: relatedObjects.count,
                                                        }
                                                    )}
                                                    <div
                                                        className={
                                                            styles[
                                                                'ObjectEntry__BannerIcon'
                                                            ]
                                                        }>
                                                        <Icon
                                                            type={'arrow'}
                                                            size={'medium'}
                                                            color={'black800'}
                                                        />
                                                    </div>
                                                </span>
                                            </div>
                                        )}

                                    <div
                                        className={
                                            styles[
                                                'ObjectEntry__ButtonsContainerDesktop'
                                            ]
                                        }>
                                        {showMoreButton && (
                                            <Button
                                                type="secondaryPink"
                                                icon={
                                                    readMore ? 'minus' : 'plus'
                                                }
                                                iconColor="pink"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleReadMore(e);
                                                }}
                                                text={
                                                    readMore
                                                        ? t('generic.showLess')
                                                        : t('generic.showMore')
                                                }
                                            />
                                        )}
                                        {readMore && (
                                            <ActionButtons
                                                isMyList={isMyList}
                                                showRemoveButton={
                                                    showRemoveButton
                                                }
                                                handleRemoveItem={
                                                    handleRemoveItem
                                                }
                                                handleAddToMyList={
                                                    handleAddToMyList
                                                }
                                                isInMyList={isInMyList}
                                                handleOpenModal={
                                                    handleOpenModal
                                                }
                                                modalIsOpen={modalIsOpen}
                                                handleClose={handleClose}
                                                title={title}
                                                label={label}
                                                href={href}
                                                id={id}
                                            />
                                        )}
                                    </div>
                                    {readMore && !!massInfo.length && (
                                        <div
                                            className={
                                                styles['ObjectEntry__MassInfo']
                                            }>
                                            {massInfo.map((info, i) => (
                                                <MassInfo key={i} {...info} />
                                            ))}
                                        </div>
                                    )}
                                </React.Fragment>
                            )}
                        </div>
                    </div>
                </div>
                {dangerousText && (
                    <div className={styles['ObjectEntry__TextMobile']}>
                        <p
                            ref={textElement}
                            className={classNames(styles['ObjectEntry__Text'], {
                                [styles['ObjectEntry__Text--Expanded']]:
                                    readMore,
                            })}
                            dangerouslySetInnerHTML={dangerousText}
                        />
                    </div>
                )}

                {!!relatedObjects.count && isGuidePage && readMore && (
                    <div
                        className={classNames(
                            styles['ObjectEntry__Banner'],
                            styles['ObjectEntry__Banner--Mobile']
                        )}>
                        <a
                            href={relatedObjects.href}
                            className={styles['ObjectEntry__BannerLink']}
                        />
                        <span className={styles['ObjectEntry__BannerText']}>
                            {t('Object.relatedObjects', {
                                count: relatedObjects.count,
                            })}
                            <div className={styles['ObjectEntry__BannerIcon']}>
                                <Icon
                                    type={'arrow'}
                                    size={'medium'}
                                    color={'black800'}
                                />
                            </div>
                        </span>
                    </div>
                )}

                <div className={styles['ObjectEntry__ButtonsContainerMobile']}>
                    {readMore && (
                        <ActionButtons
                            isMyList={isMyList}
                            showRemoveButton={showRemoveButton}
                            handleRemoveItem={handleRemoveItem}
                            handleAddToMyList={handleAddToMyList}
                            isInMyList={isInMyList}
                            handleOpenModal={handleOpenModal}
                            modalIsOpen={modalIsOpen}
                            handleClose={handleClose}
                            title={title}
                            label={label}
                            href={href}
                            id={id}
                        />
                    )}
                </div>

                {readMore && (
                    <div
                        className={classNames(
                            styles['ObjectEntry__InfoBoxes'],
                            {
                                [styles['ObjectEntry__InfoBoxes--NoPadding']]:
                                    !!massInfo.length,
                            }
                        )}>
                        {!!occurrences && occurrences.length > 1 && (
                            <div className={styles['ObjectEntry__Occurrences']}>
                                <ListOccurrences occurrences={occurrences} />
                            </div>
                        )}
                        {infoBox && (
                            <InfoBox
                                {...infoBox}
                                closestStation={closestStation}
                                modifiers={[isGreyInfoBox, 'PinkBorder']}
                                heading={t('EventPageContent.gettingThere')}
                            />
                        )}
                    </div>
                )}
            </div>
        );
    }
);

ObjectEntry.propTypes = {
    id: PropTypes.number,
    title: PropTypes.string,
    slug: PropTypes.string,
    href: PropTypes.string,
    image: PropTypes.object,
    text: PropTypes.string,
    label: PropTypes.string,
    removeItem: PropTypes.func,
    showRemoveButton: PropTypes.bool,
    isInMyList: PropTypes.bool,
    active: PropTypes.bool,
    onClick: PropTypes.func,
    onMouseOver: PropTypes.func,
    addToMyList: PropTypes.func,
    isMyList: PropTypes.bool,
    massInfo: PropTypes.array,
    date: PropTypes.string,
};

const ActionButtons = ({
    isMyList,
    showRemoveButton,
    handleRemoveItem,
    handleAddToMyList,
    isInMyList,
    handleOpenModal,
    modalIsOpen,
    handleClose,
    title,
    label,
    href,
    id,
}) => {
    const { t } = useTranslation();
    return (
        <div className={styles['ObjectEntry__ButtonContainer']}>
            <button
                className={styles['ObjectEntry__Button']}
                aria-label="Share"
                type="button"
                onClick={handleOpenModal}>
                <LabeledIcon
                    aria-live="polite"
                    type="share"
                    label={t('generic.share')}
                    color="black"
                    iconSize="large"
                    modifiers={[styles['ObjectEntry__LabeledIcon']]}
                />
            </button>

            {!isMyList && (
                <Button
                    type="secondary"
                    onClick={handleAddToMyList}
                    isClicked={isInMyList}
                    icon="bookmark"
                    iconColor="black800"
                    size="small"
                    iconSize="small"
                    text={isInMyList ? t('generic.saved') : t('generic.save')}
                />
            )}

            {isMyList && showRemoveButton && (
                <div className={styles['ObjectEntry__RemoveButtonMobile']}>
                    <button
                        className={styles['ObjectEntry__RemoveButton']}
                        aria-label="Remove"
                        type="button"
                        size="small"
                        onClick={() => {
                            handleRemoveItem(id);
                        }}>
                        <div
                            className={styles['ObjectEntry__RemoveButtonIcon']}>
                            <CloseIcon />
                        </div>
                        <div className={styles['ObjectEntry__ButtonLabel']}>
                            {t('generic.remove')}
                        </div>
                    </button>
                </div>
            )}

            <a
                className={styles['ObjectEntry__Button']}
                href={href}
                onClick={(e) => e.stopPropagation()}>
                <span className="sr-only">{title}</span>
            </a>

            <ShareModal
                title={title}
                categories={label}
                isOpen={modalIsOpen}
                url={href}
                handleClose={handleClose}
            />
        </div>
    );
};

export default ObjectEntry;
